import { Table, Typography } from 'antd';
import { UserAddOutlined } from '@ant-design/icons';
import { TableProps } from 'antd/es/table';
import Button from 'components/new-ui/base/button/Button';
import EmptyCell from 'components/new-ui/base/emtpy-cell/EmptyCell';
import { StyledTableHeaderCell, StyledTableWrapper } from 'components/new-ui/base/table/styles';
import { ListPayload } from 'core/http/response';
import { Team, TeamStatuses } from 'store/teams/types';
import TeamStatusTag from 'views/teams/components/TeamStatusTag';
import { TeamsSortParams } from 'views/teams/useTeamsFilters';
import { declarationCountryDisplay } from 'store/declarations/enums/common/declaration-country';
import { SortDirection, toAntOrder } from 'utils/tableHelpers';
import { IndividualType } from 'store/individuals/individual';
import { getTeamManagersLabel } from 'views/teams/utils';

interface Props<T> extends TableProps<Team> {
    canAssign: boolean;
    teams: ListPayload<Team> | undefined;
    loading?: boolean;
    currentSortParam?: TeamsSortParams;
    currentSortDirection?: SortDirection;
    onRowClick?: (record: T) => void;
    onAssignTeam?: (record: T) => void;
}

const TeamsTable = ({
    teams,
    loading,
    canAssign,
    currentSortDirection,
    currentSortParam,
    onRowClick,
    onAssignTeam,
    ...rest
}: Props<Team>) => {
    return (
        <StyledTableWrapper
            style={{
                marginTop: 24,
            }}
        >
            <Table<Team>
                rowKey="id"
                loading={loading}
                dataSource={teams?.list}
                columns={[
                    {
                        title: 'Team Name',
                        key: TeamsSortParams.NAME,
                        dataIndex: 'name',
                        sorter: true,
                        sortOrder: currentSortParam === TeamsSortParams.NAME ? toAntOrder(currentSortDirection) : null,
                        render: (name: string) => {
                            return <Typography data-testid="teams-table-team-name-row">{name}</Typography>;
                        },
                    },
                    {
                        title: 'Team Manager',
                        key: 'TEAM_MANAGER',
                        render: (_, record) => {
                            const dataTestId = 'teams-table-team-manager-row';

                            const teamManagers =
                                record.members.filter((member) => member.role === IndividualType.TEAM_MANAGER) ?? [];

                            if (!teamManagers.length) {
                                if (canAssign) {
                                    return (
                                        <Button
                                            data-testid={dataTestId}
                                            type="assign"
                                            title="Assign Team"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onAssignTeam?.(record);
                                            }}
                                        />
                                    );
                                }

                                return <EmptyCell dataTestId={dataTestId} />;
                            }

                            const label = getTeamManagersLabel(teamManagers);
                            return <Typography data-testid={dataTestId}>{label}</Typography>;
                        },
                    },
                    {
                        title: 'Members',
                        key: TeamsSortParams.MEMBERS_COUNT,
                        sorter: true,
                        sortOrder:
                            currentSortParam === TeamsSortParams.MEMBERS_COUNT
                                ? toAntOrder(currentSortDirection)
                                : null,
                        render: (_, record) => {
                            const dataTestId = 'teams-table-num-members-row';
                            const numMembers = record.members.length;
                            return <Typography data-testid={dataTestId}>{numMembers}</Typography>;
                        },
                    },
                    {
                        title: 'Market',
                        key: TeamsSortParams.MARKET,
                        dataIndex: 'market',
                        sorter: true,
                        sortOrder:
                            currentSortParam === TeamsSortParams.MARKET ? toAntOrder(currentSortDirection) : null,
                        render: (market?: string) => {
                            const dataTestId = 'teams-table-market-row';
                            if (!market) {
                                return <EmptyCell dataTestId={dataTestId} />;
                            }
                            return (
                                <Typography data-testid={dataTestId}>
                                    {declarationCountryDisplay[market as keyof typeof declarationCountryDisplay]}
                                </Typography>
                            );
                        },
                    },
                    {
                        title: 'Customers',
                        key: TeamsSortParams.CUSTOMERS_COUNT,
                        sorter: true,
                        sortOrder:
                            currentSortParam === TeamsSortParams.CUSTOMERS_COUNT
                                ? toAntOrder(currentSortDirection)
                                : null,
                        dataIndex: 'customersCount',
                        render: (customersCount: number) => {
                            const dataTestId = 'teams-table-customers-row';
                            if (!customersCount?.toString()) {
                                return <EmptyCell dataTestId={dataTestId} />;
                            }
                            return <Typography data-testid={dataTestId}>{customersCount}</Typography>;
                        },
                    },
                    {
                        title: 'Status',
                        key: 'STATUS',
                        sorter: true,
                        sortOrder:
                            currentSortParam === TeamsSortParams.STATUS ? toAntOrder(currentSortDirection) : null,
                        dataIndex: 'status',
                        render: (status: TeamStatuses) => {
                            const dataTestId = 'teams-table-status-row';
                            return <TeamStatusTag data-testid={dataTestId} status={status} />;
                        },
                    },
                ]}
                onRow={(record) => ({
                    onClick: () => onRowClick?.(record),
                    style: onRowClick ? { cursor: 'pointer' } : undefined,
                })}
                components={{
                    header: {
                        cell: (props: any) => <StyledTableHeaderCell {...props} />,
                    },
                }}
                {...rest}
            />
        </StyledTableWrapper>
    );
};

export default TeamsTable;
